import { Box, Stack, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { TextCellWithTooltip } from "components/DataGridCell";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import { ReactNode, useMemo } from "react";
import { SectionCard } from "routes/Customer/Waste/Containers/containerSections";
import {
  formatInvoiceDate,
  formatInvoiceMoney,
  IMPORT_VENDOR_LABELS,
  LINE_KIND_LABELS,
  NO_VALUE,
} from "routes/Customer/Waste/Invoices/utils";

import { ExceptionRow, STATUS_COLOUR } from "./columns";
import { InvoiceLinks } from "./InvoiceLinks";
import {
  facilityLabel,
  formatDaysOutside,
  formatGradedSpan,
  formatWindow,
  KIND_LABELS,
  STATUS_LABELS,
  UNKNOWN_KIND_LABEL,
} from "./labels";

type InvoiceLine = NonNullable<ExceptionRow["visit"]>["invoiceLines"][number];

function Heading({ children }: { children: ReactNode }) {
  return (
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {children}
    </Typography>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: "6.5rem", flex: "none" }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ minWidth: 0 }}>
        {children}
      </Typography>
    </Stack>
  );
}

/**
 * One side of the pairing, tinted rather than bordered so it reads apart
 * without a third border.
 */
function Side({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ flex: "1 1 0", minWidth: "15rem" }}>
      <SectionCard title={title}>{children}</SectionCard>
    </Box>
  );
}

function Absent({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}

/**
 * The published window, plus the days graded against where its name does not
 * state them.
 */
function Window({ start, end }: { start?: string | null; end?: string | null }) {
  const graded = formatGradedSpan(start, end);
  return (
    <>
      {formatWindow(start, end)}
      {graded == null ? null : (
        <Typography variant="caption" color="text.secondary" display="block">
          {`Graded against ${graded}`}
        </Typography>
      )}
    </>
  );
}

function ScheduleSide({ row }: { row: ExceptionRow }) {
  if (row.occurrence == null) {
    return (
      <Side title="What the schedule planned">
        <Absent>No published window covers this facility for this service in this period.</Absent>
      </Side>
    );
  }
  return (
    <Side title="What the schedule planned">
      <Field label="Window">
        <Window
          start={row.occurrence.scheduledWindowStart}
          end={row.occurrence.scheduledWindowEnd}
        />
      </Field>
      <Field label="Service">
        {row.occurrence.kind ? KIND_LABELS[row.occurrence.kind] : UNKNOWN_KIND_LABEL}
      </Field>
      {row.occurrence.vendorServiceOrderNumber == null ? null : (
        <Field label="Order number">{row.occurrence.vendorServiceOrderNumber}</Field>
      )}
    </Side>
  );
}

function InvoiceSide({ row }: { row: ExceptionRow }) {
  if (row.visit == null) {
    return (
      <Side title="What the invoices showed">
        <Absent>No invoice line in this period names this window or falls inside it.</Absent>
      </Side>
    );
  }
  return (
    <Side title="What the invoices showed">
      <Field label="Serviced">{formatInvoiceDate(row.visit.serviceDate)}</Field>
      <Field label="Timing">{formatDaysOutside(row.daysOutside)}</Field>
      {row.visit.serviceOrderNumber == null ? null : (
        <Field label="Order number">{row.visit.serviceOrderNumber}</Field>
      )}
      <Field label="Manifest">{row.visit.manifestNumber ?? NO_VALUE}</Field>
      <Field label="Invoices">
        <InvoiceLinks visit={row.visit} />
      </Field>
      <Field label="Billed">{formatInvoiceMoney(row.visit.billedAmount)}</Field>
    </Side>
  );
}

const INVOICE_LINE_COLUMNS: CustomGridColDef<InvoiceLine>[] = [
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth: 220,
    // A vendor's own wording runs long; the cell keeps one line and the full
    // text is a hover away rather than lost.
    renderCell: ({ value }) => <TextCellWithTooltip>{value ?? NO_VALUE}</TextCellWithTooltip>,
  },
  {
    field: "kind",
    headerName: "Billed as",
    width: 116,
    // The same wording the invoice's own line table uses, rather than the enum.
    valueGetter: (_value, line) => LINE_KIND_LABELS[line.kind] ?? line.kind,
  },
  { field: "code", headerName: "Code", width: 128 },
  {
    field: "serviceDate",
    headerName: "Service date",
    width: 112,
    // A visit grouped by service order can span days, so a line carries its own
    // date rather than borrowing the visit's.
    valueGetter: (_value, line) => formatInvoiceDate(line.serviceDate),
  },
  {
    field: "billedAmount",
    headerName: "Amount",
    width: 100,
    align: "right",
    headerAlign: "right",
    // The wire value is an exact decimal string, so sort on a number rather than
    // letting the grid compare it lexically.
    type: "number",
    valueGetter: (_value, line) => safeParseFloat(line.billedAmount),
    valueFormatter: (value: number | null) => formatInvoiceMoney(value),
  },
];

/**
 * How the two records were paired, as distinct from what the pairing concluded.
 * Shown only when both sides exist.
 */
function MatchEvidence({ row }: { row: ExceptionRow }) {
  const { occurrence, visit } = row;
  if (occurrence == null || visit == null) return null;

  // Only a vendor that issues order numbers has this to say; where neither
  // side names one the row is left out rather than reporting an absence.
  const billed = visit.serviceOrderNumber;
  const scheduled = occurrence.vendorServiceOrderNumber;
  const order = (() => {
    if (billed != null && scheduled != null) {
      return billed === scheduled
        ? { basis: "Both name the same number", detail: billed }
        : { basis: "Different numbers", detail: `${billed} billed, ${scheduled} scheduled` };
    }
    if (billed != null) return { basis: "Named by the invoice only", detail: billed };
    if (scheduled != null) return { basis: "Named by the schedule only", detail: scheduled };
    return null;
  })();

  const sameFacility =
    row.customerFacilityId != null && row.customerFacilityId === occurrence.customerFacilityId;
  const facility = sameFacility
    ? { basis: "Same facility identifier", detail: row.customerFacilityId }
    : // They matched on one of the four identifiers or they would not be paired,
      // and the row carries only the customer's identifier, so the basis is not
      // named rather than guessed at.
      { basis: "Matched on another site identifier", detail: null };

  const sameKind = row.kind === occurrence.kind;
  const service = sameKind
    ? { basis: "Same service", detail: KIND_LABELS[occurrence.kind] }
    : {
        basis: "Different service",
        detail: `${row.kind ? KIND_LABELS[row.kind] : UNKNOWN_KIND_LABEL} billed, ${KIND_LABELS[occurrence.kind]} scheduled`,
      };

  return (
    <Box sx={{ mt: 3 }}>
      <Heading>How these were matched</Heading>
      <Stack spacing={0.5}>
        {[
          ...(order == null ? [] : [{ label: "Order number", ...order }]),
          { label: "Facility", ...facility },
          { label: "Service", ...service },
        ].map(({ label, basis, detail }) => (
          <Field key={label} label={label}>
            <Typography variant="body2" component="span">
              {basis}
            </Typography>
            {detail == null ? null : (
              <Typography variant="body2" component="span" color="text.secondary">
                {" \u00b7 "}
                {detail}
              </Typography>
            )}
          </Field>
        ))}
      </Stack>
    </Box>
  );
}

function InvoiceLines({ row }: { row: ExceptionRow }) {
  const lines = useMemo(() => row.visit?.invoiceLines ?? [], [row.visit]);
  if (lines.length === 0) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Heading>Invoice lines</Heading>
      <DataGrid<InvoiceLine>
        rows={lines}
        columns={INVOICE_LINE_COLUMNS}
        getRowId={(line) => line.id}
        autoHeight
        // The premium grid does not page unless asked, and a visit can carry
        // fifty lines.
        pagination
        disableColumnMenu
        disableRowSelectionOnClick
        // Five rows keep the reasons on screen; largest first so the first page
        // explains the total.
        initialState={{
          pagination: { paginationModel: { pageSize: 5, page: 0 } },
          sorting: {
            sortModel: [
              { field: "billedAmount", sort: "desc" },
              // One visit can carry the same charge twice at the same price.
              { field: "description", sort: "asc" },
            ],
          },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
      />
    </Box>
  );
}

/**
 * Every window that could have accounted for the visit. Shown only when more
 * than one could, because that is the evidence behind a match the audit could
 * not settle — with one candidate it is just the occurrence again.
 */
function Candidates({ row }: { row: ExceptionRow }) {
  if (row.candidates.length < 2) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Heading>Windows that could have answered</Heading>
      <Stack spacing={0.5}>
        {row.candidates.map((candidate) => (
          <Typography key={candidate.id} variant="body2">
            {formatWindow(candidate.scheduledWindowStart, candidate.scheduledWindowEnd)}
            {candidate.vendorServiceOrderNumber ? ` · ${candidate.vendorServiceOrderNumber}` : ""}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export function ResultDrawer({ row, onClose }: { row: ExceptionRow | null; onClose: () => void }) {
  return (
    <RightDrawerShell
      open={row != null}
      onClose={onClose}
      width={{ sm: 640, md: 928 }}
      title={
        row == null ? null : (
          <Typography variant="h6" component="h2" sx={{ whiteSpace: "normal" }}>
            {[
              facilityLabel(row.facilityName, row.customerFacilityId) ?? "Unidentified facility",
              row.kind ? KIND_LABELS[row.kind] : UNKNOWN_KIND_LABEL,
            ].join(" · ")}
          </Typography>
        )
      }
    >
      {row == null ? null : (
        <>
          <Stack direction="row" alignItems="center" spacing={1}>
            <StatusChip color={STATUS_COLOUR[row.status]} label={STATUS_LABELS[row.status]} />
            <Typography variant="body2" color="text.secondary">
              {IMPORT_VENDOR_LABELS[row.vendor]}
            </Typography>
          </Stack>

          <Typography variant="body2" sx={{ mt: 2 }}>
            {row.explanation}
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="stretch"
            sx={{ mt: 2.5 }}
          >
            <ScheduleSide row={row} />
            <InvoiceSide row={row} />
          </Stack>

          <MatchEvidence row={row} />
          <InvoiceLines row={row} />
          <Candidates row={row} />

          {row.issues.length === 0 ? null : (
            <Box sx={{ mt: 3 }}>
              <Heading>What to ask for</Heading>
              <Stack spacing={1.5}>
                {row.issues.map((issue) => (
                  <Box key={issue.code}>
                    <Typography variant="body2">{issue.message}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {issue.clarification}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}
    </RightDrawerShell>
  );
}
